/**
 * Typed provider user management API facade over the generated contract client.
 */

import * as dt from '@cnapse/shared/data.types.js';
import { providerUsersApi } from '../api/generated/providerUsers.api.js';
import { apiClient } from '../api/ApiClient.js';
import type {
  ChangeProviderUserRoleRequest,
  CreateProviderUserRequest,
  GetProviderUsersRequest,
  ProviderUser,
  UpdateProviderUserRequest,
} from '@cnapse/shared/schemas.js';

class ProviderUsersService {
  async listProviderUsers(params?: GetProviderUsersRequest): Promise<ProviderUser[]> {
    return providerUsersApi.list(params ?? {}) as Promise<ProviderUser[]>;
  }

  async getProviderUser(userId: dt.IntegerId): Promise<ProviderUser> {
    return providerUsersApi.getById(userId) as Promise<ProviderUser>;
  }

  async createProviderUser(userData: CreateProviderUserRequest): Promise<{
    user: ProviderUser;
    temporaryPassword: string;
  }> {
    return providerUsersApi.create(userData) as Promise<{
      user: ProviderUser;
      temporaryPassword: string;
    }>;
  }

  async updateProviderUser(
    userId: dt.IntegerId,
    userData: UpdateProviderUserRequest,
    stepUpToken?: string,
  ): Promise<ProviderUser> {
    if (stepUpToken) {
      return apiClient.withStepUpToken(stepUpToken, () =>
        providerUsersApi.update(userId, userData),
      ) as Promise<ProviderUser>;
    }
    return providerUsersApi.update(userId, userData) as Promise<ProviderUser>;
  }

  async changeProviderUserStatus(
    userId: dt.IntegerId,
    isActive: boolean,
    stepUpToken: string,
  ): Promise<ProviderUser> {
    return apiClient.withStepUpToken(stepUpToken, () =>
      providerUsersApi.changeStatus(userId, { isActive }),
    ) as Promise<ProviderUser>;
  }

  async changeProviderUserRole(
    userId: dt.IntegerId,
    role: ChangeProviderUserRoleRequest['role'],
    stepUpToken: string,
  ): Promise<ProviderUser> {
    return apiClient.withStepUpToken(stepUpToken, () =>
      providerUsersApi.changeRole(userId, { role }),
    ) as Promise<ProviderUser>;
  }

  async deleteProviderUser(userId: dt.IntegerId, stepUpToken: string): Promise<void> {
    await apiClient.withStepUpToken(stepUpToken, () => providerUsersApi.delete(userId));
  }

  async resendProviderUserInvitation(
    userId: dt.IntegerId,
    stepUpToken: string,
  ): Promise<{ email: string }> {
    return apiClient.withStepUpToken(stepUpToken, () =>
      providerUsersApi.resendInvitation(userId),
    ) as Promise<{ email: string }>;
  }
}

export const providerUsersService = new ProviderUsersService();
